import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { AppDispatch, GetState, RootState } from "../../store";

import { getTaskActivities, getUserActivities } from "../../lib/api";
import { getAllFromTokenEndpoint } from "../../utilities";
import { AddErrorAlert } from "../alert";
import { cleanTaskActivityData, cleanUserActivityData } from "./helpers";

type ActivityState = {
  task: Record<string, TaskActivityResponse[]>;
  user: Record<string, UserActivityResponse[]>;
  loading: string[];
  loaded: string[];
};

const initialState: ActivityState = {
  task: {},
  user: {},
  loading: [],
  loaded: [],
};

const activitySlice = createSlice({
  name: "activity",
  initialState,
  reducers: {
    startLoad: (state: ActivityState, action: PayloadAction<string>) => {
      state.loading.push(action.payload);
    },

    finishTaskLoad: (
      state: ActivityState,
      action: PayloadAction<{ id: string; data: TaskActivityResponse[] }>,
    ) => {
      const { id, data } = action.payload;
      state.loaded.push(id);
      state.task[id] = data;
    },

    finishUserLoad: (
      state: ActivityState,
      action: PayloadAction<{ id: string; data: UserActivityResponse[] }>,
    ) => {
      const { id, data } = action.payload;
      state.loaded.push(id);
      state.user[id] = data;
    },
  },
});

const { startLoad, finishTaskLoad, finishUserLoad } = activitySlice.actions;

export const loadTaskActivities = (id: string, force: boolean = false) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { loading, loaded } = selectActivity(getState());
    if (!force && (loading.includes(id) || loaded.includes(id))) {
      return;
    }

    try {
      dispatch(startLoad(id));
      const fetchFunction = async (after: string | null) => {
        return await getTaskActivities({ id, after });
      };
      const activity = await getAllFromTokenEndpoint(fetchFunction);
      const cleaned = cleanTaskActivityData(activity);
      dispatch(finishTaskLoad({ id, data: cleaned }));
    } catch (err) {
      dispatch(AddErrorAlert(err));
      dispatch(finishTaskLoad({ id, data: [] }));
    }
  };
};

export const loadUserActivities = (id: string, force: boolean = false) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { loading, loaded } = selectActivity(getState());
    if (!force && (loading.includes(id) || loaded.includes(id))) {
      return;
    }

    try {
      dispatch(startLoad(id));
      const fetchFunction = async (after: string | null) => {
        return await getUserActivities({ id, after });
      };
      const activity = await getAllFromTokenEndpoint(fetchFunction);
      const cleaned = cleanUserActivityData(activity);
      dispatch(finishUserLoad({ id, data: cleaned }));
    } catch (err) {
      dispatch(AddErrorAlert(err));
      dispatch(finishUserLoad({ id, data: [] }));
    }
  };
};

export const selectActivity = (state: RootState) => state.activity;

export default activitySlice.reducer;
